<template>
  <div class="member-user-filer-panel" v-bind:class="{ changing: isChanging }">
    <div v-show="isChanging" class="filters">
      <user-status-filter-type
        v-if="isAvailable.userType"
        v-bind="localCondition.type"
        v-on:changed="$_onChanged('userType', $event)"
      />
      <user-status-filter-organization
        v-if="isAvailable.organization"
        v-bind="localCondition.organization"
        v-on:changed="$_onChanged('organization', $event)"
      />
      <user-status-filter-indoor
        v-if="isAvailable.indoor"
        v-bind="localCondition.indoor"
        v-on:changed="$_onChanged('indoor', $event)"
      />
      <user-status-filter-place
        v-if="isAvailable.place"
        v-bind="{ ...localCondition.place, ...localCondition.indoor }"
        v-on:changed="$_onChanged('place', $event)"
      />
      <user-status-filter-status
        v-if="isAvailable.status"
        v-bind="localCondition.status"
        v-on:changed="$_onChanged('status', $event)"
      />
      <user-status-filter-activity
        v-if="isAvailable.activity"
        v-bind="localCondition.activity"
        v-on:changed="$_onChanged('activity', $event)"
      />
      <user-status-filter-positioning
        v-if="isAvailable.positioning"
        v-bind="localCondition.positioning"
        v-on:changed="$_onChanged('positioning', $event)"
      />
      <user-status-filter-sensor
        v-if="isAvailable.sensor"
        v-bind="localCondition.sensor"
        v-on:changed="$_onChanged('sensor', $event)"
      />
    </div>
    <div class="action">
      <h4
        class="title"
        v-bind:class="{ hide: isChanging }"
        v-t.preserve="'appMemberUserFilterPanel.condition'"
      />
      <a class="toggle" v-on:click="isChanging = !isChanging">
        <icon-arrow-down
          class="icon-arrow"
          v-bind:class="{ reverse: isChanging }"
        />
        <span class="toggle-text">
          {{ toggleText }}
        </span>
      </a>
    </div>
    <div v-if="resultText" v-show="!isChanging" class="result">
      {{ resultText }}
    </div>
  </div>
</template>

<script>
import { CONDITION_AVAILABLE_FUNCTION_TYPE } from '@/composables/member/UseUserStatusFilterCondition'
import {
  UserStatusFilterOrganization,
  UserStatusFilterIndoor,
  UserStatusFilterPlace,
  UserStatusFilterStatus,
  UserStatusFilterActivity,
  UserStatusFilterPositioning,
  UserStatusFilterSensor,
  UserStatusFilterType,
} from '@/components/user/filteritem'
import IconArrowDown from '@/assets/ifield/icon_arrow_down.svg?component'

export default {
  name: 'AppMemberUserFilterPanel',
  components: {
    UserStatusFilterOrganization,
    UserStatusFilterIndoor,
    UserStatusFilterPlace,
    UserStatusFilterStatus,
    UserStatusFilterActivity,
    UserStatusFilterPositioning,
    UserStatusFilterSensor,
    UserStatusFilterType,
    IconArrowDown,
  },
  props: {
    /**
     * 絞り込み条件
     */
    condition: {
      type: Object,
      default: () => {},
    },
  },
  emits: [
    /**
     * 条件変更イベント
     * @param {Object} condition 条件
     */
    'condition-changed',
  ],
  data: function () {
    return {
      // 条件変更中の場合はtrue
      isChanging: false,
      // ローカル保持用絞り込み条件
      localCondition: this.condition,
      // 結果表示テキスト
      text: {
        organization: '',
        indoor: '',
        place: '',
        status: '',
        activity: '',
        positioning: '',
        sensor: '',
      },
    }
  },
  computed: {
    /**
     * 各絞り込み条件の利用可能フラグ
     * @type {Object}
     */
    isAvailable: function () {
      return Object.fromEntries(
        [...Object.entries(CONDITION_AVAILABLE_FUNCTION_TYPE)].map(
          ([key, type]) => [key, !!this.$store.getters.functionType(type)]
        )
      )
    },
    /**
     * 表示トグルリンクのテキスト
     * @type {string}
     */
    toggleText: function () {
      if (this.isChanging) {
        return this.$t('appMemberUserFilterPanel.action.close')
      } else if (this.resultText.length) {
        return this.$t('appMemberUserFilterPanel.action.change')
      } else {
        return this.$t('appMemberUserFilterPanel.action.set')
      }
    },
    /**
     * 結果表示テキスト
     * @type {String}
     */
    resultText: function () {
      return [...Object.values(this.text)].filter((t) => t).join(', ')
    },
    /**
     * props.condition と localCondition が値レベルで変わっている場合はtrue
     * @type {Boolean}
     */
    isConditionValueDifferent: function () {
      return (
        JSON.stringify(this.condition) !== JSON.stringify(this.localCondition)
      )
    },
  },
  watch: {
    condition: {
      deep: true,
      handler: function (value) {
        this.localCondition = value
      },
    },
    localCondition: function (value) {
      if (this.isConditionValueDifferent) {
        this.$emit('condition-changed', value)
      }
    },
  },
  methods: {
    /**
     * 条件変更時処理
     * @param {String} type 絞り込み条件種類
     * @param {Object} result 変更結果
     */
    $_onChanged: function (type, result) {
      const { value, text } = result
      this.text[type] = text
      this.localCondition = {
        ...this.localCondition,
        [type]: value,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$-padding-horizontal: 12px;

.member-user-filer-panel {
  width: 100%;
  background-color: #eaecef;
  padding: 10px $-padding-horizontal 10px $-padding-horizontal;
  overflow: hidden;
  display: grid;
  grid-template-rows: 1.25rem minmax(0, 1fr);

  &.changing {
    grid-template-rows: minmax(0, 1fr) 1.25rem;
    padding: 0 $-padding-horizontal 10px $-padding-horizontal;
  }
}

.filters {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  justify-items: left;
  gap: 8px;
  padding: 10px 12px 8px 0;
  overflow-x: hidden;
  overflow-y: auto;
  // スクロールバー発生時に右側のpadding部に出すための設定
  width: calc(100% + $-padding-horizontal);
  margin-right: -$-padding-horizontal;
  @include mixin-scrollbar(#eaecef, #d0d0d0);
  & {
    scrollbar-gutter: stable;
  }

  .label {
    font-size: $font-size-small;
    font-weight: $font-weight-bold;
    margin: 0;
    white-space: nowrap;
    line-height: 2rem;
  }
}

.action {
  display: flex;
  justify-content: space-between;
  align-items: center;
  line-height: 1;

  .title {
    color: $text-color-weak;
    font-size: $font-size-middle;
    font-weight: $font-weight-bold;
    margin: 0;

    &.hide {
      opacity: 0;
    }
  }

  .toggle {
    &:hover {
      text-decoration: $text-color-link underline;
      cursor: pointer;
    }
  }

  .icon-arrow {
    fill: none;
    stroke: $text-color-link;
    stroke-width: 1.5px;
    margin: -8px -4px;
    transition: transform 0.3s;

    &.reverse {
      transform: rotate(180deg);
    }
  }

  .toggle-text {
    color: $text-color-link;
    font-size: $font-size-middle;
    font-weight: $font-weight-bold;
  }
}

.result {
  font-size: $font-size-small;
  text-align: left;
  padding-top: 8px;
}
</style>
